import React, { useEffect, useState } from "react";
import theme from "../../styles/theme/theme";
import {
  FaBox,
  FaGavel,
  FaFileAlt,
  FaCreditCard,
  FaCog,
  FaMoneyBill,
  FaHandshake,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  getNotificationList,
  notificationsClearAll,
  notificationsMarkRead,
} from "../../features/user/userSlice";
import { timeAgo } from "../../utils/timeAgo";
import Loader from "../../components/common/Loader";

const typeIcons = {
  Order: <FaBox />,
  Bid: <FaGavel />,
  Kyc: <FaFileAlt />,
  Payment: <FaCreditCard />,
  CounteOffer: <FaHandshake />,
  PriceUpdate: <FaMoneyBill />,
  Other: <FaCog />,
};

const getTypeColor = (type) => {
  switch (type) {
    case "Order":
      return theme.palette.info.main;
    case "Bid":
      return theme.palette.success.main;
    case "Kyc":
      return theme.palette.warning.main;
    case "Payment":
      return theme.palette.primary.main;
    case "Counter offer":
      return theme.palette.secondary.main;
    case "PriceUpdate":
      return theme.palette.success.dark;
    case "Other":
      return theme.palette.grey || theme.palette.text.secondary;
    default:
      return theme.palette.primary.main;
  }
};

const Notification = () => {
  const dispatch = useDispatch();
  const notifications = useSelector((state) => state.user.notifications || []);
  const notificationLoading = useSelector(
    (state) => state.user.notificationLoading
  );

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    dispatch(getNotificationList())
      .unwrap()
      .catch((error) => {
        console.error("Failed to load notifications", error);
      });
  }, [dispatch]);

  const handleMarkAsRead = (id) => {
    dispatch(notificationsMarkRead(id)).unwrap();
  };

  const handleClearAll = () => {
    dispatch(notificationsClearAll()).unwrap();
  };

  const paginatedNotifications = notifications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(notifications.length / itemsPerPage);

  if (notificationLoading) {
    return <Loader />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: theme.palette.background.paper,
        fontFamily: theme.typography.fontFamily,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between", // <-- makes pagination stick to bottom
        padding: `${theme.spacing(6)} ${theme.spacing(2)}`,
      }}
    >
      <div>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            padding: `0 ${theme.spacing(2)}`,
            marginBottom: theme.spacing(4),
          }}
        >
          <h2
            style={{
              ...theme.typography.h3,
              color: theme.palette.text.primary,
              letterSpacing: 1,
              margin: 0,
              flex: 1,
            }}
          >
            Notifications
          </h2>
          {notifications.length > 0 && (
            <button
              onClick={handleClearAll}
              style={{
                padding: "8px 14px",
                background: "#fff",
                color: theme.palette.primary.main,
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 6,
                cursor: "pointer",
                fontSize: "0.95rem",
                fontWeight: 600,
                transition: "background 0.2s, color 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = theme.palette.primary.main;
                e.currentTarget.style.color = "#fff";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.color = theme.palette.primary.main;
              }}
            >
              Clear All
            </button>
          )}
        </div>

        {/* Notification List */}
        <div style={{ width: "100%", maxWidth: "100%", padding: 0 }}>
          {notifications.length === 0 ? (
            <div
              style={{
                ...theme.typography.body1,
                color: theme.palette.text.secondary,
                textAlign: "center",
                marginTop: theme.spacing(8),
              }}
            >
              No notifications yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {paginatedNotifications.map((note) => (
                <div
                  key={note.id}
                  onClick={() => !note.is_read && handleMarkAsRead(note.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: theme.palette.background.paper,
                    border: `1px solid ${
                      note.is_read
                        ? theme.palette.divider || "#ececec"
                        : getTypeColor(note.type)
                    }`,
                    borderRadius: 10,
                    padding: `${theme.spacing(3)} ${theme.spacing(3)}`,
                    marginBottom: theme.spacing(3),
                    boxShadow: note.is_read
                      ? theme.shadows[0]
                      : theme.shadows[2],
                    gap: theme.spacing(2),
                    position: "relative",
                    width: "100%",
                    cursor: !note.is_read ? "pointer" : "default",
                    maxWidth: "100vw",
                  }}
                >
                  <div
                    style={{
                      fontSize: 26,
                      background: getTypeColor(note.type),
                      color: theme.palette.getContrastText(
                        getTypeColor(note.type)
                      ),
                      borderRadius: "50%",
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: theme.shadows[1],
                      flexShrink: 0,
                      marginRight: theme.spacing(1.5),
                    }}
                  >
                    {typeIcons[note.type] || "🔔"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        ...theme.typography.subtitle1,
                        fontWeight: note.is_read ? 500 : 700,
                        color: theme.palette.text.primary,
                        marginBottom: 2,
                        display: "flex",
                        alignItems: "center",
                        wordBreak: "break-word",
                      }}
                    >
                      {!note.is_read && (
                        <span
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: getTypeColor(note.type),
                            marginRight: theme.spacing(1),
                            boxShadow: `0 0 0 2px #fff`,
                          }}
                        />
                      )}
                      {note.title}
                    </div>
                    <div
                      style={{
                        ...theme.typography.body2,
                        color: theme.palette.text.secondary,
                        marginBottom: 2,
                        fontSize: "1.01rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {note.message}
                    </div>
                    <div
                      style={{
                        ...theme.typography.caption,
                        color: theme.palette.text.disabled,
                        fontSize: "0.97em",
                        marginTop: 2,
                        letterSpacing: 0.1,
                      }}
                    >
                      {timeAgo(note.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pagination at the bottom */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: `${theme.spacing(3)} 0`,
            gap: theme.spacing(2),
          }}
        >
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{
              padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
              background: theme.palette.primary.main,
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              opacity: currentPage === 1 ? 0.6 : 1,
            }}
          >
            Prev
          </button>
          <span
            style={{
              ...theme.typography.body2,
              color: theme.palette.text.primary,
            }}
          >
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            style={{
              padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
              background: theme.palette.primary.main,
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              opacity: currentPage === totalPages ? 0.6 : 1,
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Notification;
